/**
 * Command Bridge
 *
 * Translates GamemasterCommand objects from the AI DM into
 * concrete store actions on the gameboard. Provider-agnostic —
 * doesn't care whether commands came from Claude or decibel-agent.
 */

import type { GamemasterCommand, HistoryEventData, PlacementZone, TokenPosition, SyncBoardCommand } from '@machina/core';
import { useChatStore } from '../stores/chatStore';
import { trpc } from '../lib/trpc';

// =============================================================================
// ZONE-BASED TOKEN PLACEMENT
// =============================================================================

/** Anchor points as fractions of map dimensions for each placement zone. */
const ZONE_ANCHORS: Record<PlacementZone, { xFrac: number; yFrac: number }> = {
  north:           { xFrac: 0.50, yFrac: 0.15 },
  south:           { xFrac: 0.50, yFrac: 0.80 },
  east:            { xFrac: 0.85, yFrac: 0.45 },
  west:            { xFrac: 0.15, yFrac: 0.45 },
  center:          { xFrac: 0.50, yFrac: 0.45 },
  scattered:       { xFrac: 0.50, yFrac: 0.40 }, // special-cased below
  around_entrance: { xFrac: 0.50, yFrac: 0.70 },
};

/**
 * Resolve pixel positions for a group of mobs based on a placement zone.
 * Map origin (0,0) is top-left. Party enters from the south (bottom).
 */
function resolveZonePositions(
  zone: PlacementZone,
  mobCount: number,
  positioning: 'melee' | 'ranged',
  mapWidthPx: number,
  mapHeightPx: number,
  gridSize: number,
): Array<{ x: number; y: number }> {
  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

  const anchor = ZONE_ANCHORS[zone];
  const centerX = anchor.xFrac * mapWidthPx;
  const centerY = anchor.yFrac * mapHeightPx;

  // Ranged mobs push further from south edge (toward north)
  const rangedShift = positioning === 'ranged' ? -2 * gridSize : 0;
  const adjustedY = clamp(centerY + rangedShift, gridSize, mapHeightPx - gridSize);

  const positions: Array<{ x: number; y: number }> = [];

  if (zone === 'scattered') {
    // Distribute across the upper 60% of the map with randomization
    for (let i = 0; i < mobCount; i++) {
      positions.push({
        x: clamp(mapWidthPx * (0.15 + Math.random() * 0.70), gridSize, mapWidthPx - gridSize),
        y: clamp(mapHeightPx * (0.10 + Math.random() * 0.50), gridSize, mapHeightPx - gridSize),
      });
    }
  } else {
    // Arrange in a grid around the anchor (max 4 columns)
    const cols = Math.min(mobCount, 4);
    for (let i = 0; i < mobCount; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const offsetX = (col - (cols - 1) / 2) * gridSize;
      const offsetY = row * gridSize;
      // Slight jitter to avoid mechanical look
      const jitterX = (Math.random() - 0.5) * 10;
      const jitterY = (Math.random() - 0.5) * 10;
      positions.push({
        x: clamp(centerX + offsetX + jitterX, gridSize, mapWidthPx - gridSize),
        y: clamp(adjustedY + offsetY + jitterY, gridSize, mapHeightPx - gridSize),
      });
    }
  }

  return positions;
}

/** Place PCs at the south edge of the map, centered horizontally. */
export function getPartyPositions(
  pcCount: number,
  mapWidthPx: number,
  mapHeightPx: number,
  gridSize: number,
): Array<{ x: number; y: number }> {
  const totalWidth = pcCount * gridSize;
  const startX = (mapWidthPx - totalWidth) / 2;
  const southY = mapHeightPx - gridSize * 2; // 2 squares from bottom

  return Array.from({ length: pcCount }, (_, i) => ({
    x: startX + i * gridSize,
    y: southY,
  }));
}

/**
 * Resolve a pixel position from a TokenPosition (fractional or zone-based).
 * Falls back to zone-based positioning when no explicit coordinates given.
 */
function resolveTokenPosition(
  pos: TokenPosition | undefined,
  index: number,
  total: number,
  positioning: 'melee' | 'ranged',
  mapW: number,
  mapH: number,
  gridSize: number,
): { x: number; y: number } {
  if (pos?.x != null && pos?.y != null) {
    const x = Math.round(pos.x * mapW);
    const y = Math.round(pos.y * mapH);
    return {
      x: Math.max(gridSize, Math.min(mapW - gridSize, x)),
      y: Math.max(gridSize, Math.min(mapH - gridSize, y)),
    };
  }
  const zone: PlacementZone = pos?.zone ?? 'north';
  const positions = resolveZonePositions(zone, total, positioning, mapW, mapH, gridSize);
  return positions[index] ?? { x: mapW / 2, y: mapH / 4 };
}

/**
 * Execute a batch of gamemaster commands in order.
 * Commands that fail are logged but don't block subsequent commands.
 */
export async function executeCommands(
  commands: GamemasterCommand[],
  campaignId: string,
): Promise<void> {
  for (const cmd of commands) {
    try {
      await executeCommand(cmd, campaignId);
    } catch (err) {
      console.error(`[CommandBridge] Failed to execute ${cmd.type}:`, err);
    }
  }
}

async function executeCommand(cmd: GamemasterCommand, campaignId: string): Promise<void> {
  switch (cmd.type) {
    case 'spawn_encounter':
      await handleSpawnEncounter(cmd);
      break;

    case 'generate_map':
      await handleGenerateMap(cmd);
      break;

    case 'request_roll':
      // Roll requests are handled via chat UI (RollRequestCard)
      // The soloGamemasterStore dispatches these as chat messages
      break;

    case 'advance_scene':
      await handleAdvanceScene(cmd, campaignId);
      break;

    case 'npc_speak':
      await handleNpcSpeak(cmd);
      break;

    case 'sync_board':
      await handleSyncBoard(cmd);
      break;

    case 'start_combat':
      await handleStartCombat(campaignId);
      break;

    case 'end_combat':
      await handleEndCombat(cmd);
      break;

    case 'reset_board':
      await handleResetBoard(cmd.reason);
      break;

    case 'award_xp':
      await handleAwardXp(cmd);
      break;

    case 'discover_clue':
      handleDiscoverClue(cmd);
      break;

    case 'update_quest':
      handleUpdateQuest(cmd);
      break;

    case 'play_sound':
      handlePlaySound(cmd);
      break;

    case 'present_choices':
      handlePresentChoices(cmd);
      break;

    case 'setup_campaign_map':
      await handleSetupCampaignMap(cmd, campaignId);
      break;

    case 'reveal_location':
      await handleRevealLocation(cmd);
      break;

    case 'move_party':
      await handleMoveParty(cmd);
      break;

    case 'place_party':
      await handlePlaceParty(cmd);
      break;

    default:
      console.warn(`[CommandBridge] Unknown command type: ${(cmd as { type: string }).type}`);
      break;
  }
}

// =============================================================================
// COMMAND HANDLERS
// =============================================================================

async function handleSpawnEncounter(cmd: GamemasterCommand & { type: 'spawn_encounter' }): Promise<void> {
  // Lazy-import stores to avoid circular deps
  const { useMobStore } = await import('../stores/mobStore');
  const { useCampaignStore } = await import('../stores/campaignStore');
  const { useMapStore } = await import('../stores/mapStore');
  const { useTokenStore } = await import('../stores/tokenStore');

  const campaignId = useCampaignStore.getState().currentCampaignId;
  if (!campaignId) return;

  // Clear previous encounter before spawning new one (prevents additive stacking)
  await useMobStore.getState().removeAllMobs();

  // Get map dimensions for zone-based placement
  const mapBounds = useMapStore.getState().getMapBoundsInPixels();
  const gridSize = useTokenStore.getState().gridSize; // 50px = 5ft
  const mapW = mapBounds?.width ?? 1000;   // fallback 100ft * 10px
  const mapH = mapBounds?.height ?? 1000;
  const zone: PlacementZone = cmd.placementZone ?? 'north';

  // Reposition existing PC tokens to the south edge
  const pcTokens = Array.from(useTokenStore.getState().tokens.values()).filter(t => t.ownerId);
  const pcPositions = getPartyPositions(pcTokens.length, mapW, mapH, gridSize);
  pcTokens.forEach((token, i) => {
    if (pcPositions[i]) {
      useTokenStore.getState().moveToken(token.id, pcPositions[i]);
    }
  });

  // Spawn each enemy group with zone-aware positioning
  for (const enemy of cmd.enemies) {
    // Infer melee vs ranged from ability scores
    const positioning: 'melee' | 'ranged' = enemy.dex >= enemy.str + 4 ? 'ranged' : 'melee';
    const positions = resolveZonePositions(zone, enemy.count, positioning, mapW, mapH, gridSize);

    for (let i = 0; i < enemy.count; i++) {
      await useMobStore.getState().spawnMob({
        campaignId,
        name: enemy.name,
        ac: enemy.ac,
        maxHp: enemy.maxHp,
        abilities: {
          str: enemy.str,
          dex: enemy.dex,
          con: enemy.con,
          int: enemy.int,
          wis: enemy.wis,
          cha: enemy.cha,
        },
      }, {
        position: positions[i],
        rollInitiative: true,
      });
    }
  }

  if (cmd.description) {
    useChatStore.getState().addSystemMessage(cmd.description);
  }
}

// Valid enum values for map generation — AI sometimes invents values outside these
const VALID_ENVIRONMENTS = new Set([
  'dungeon_stone','dungeon_crypt','dungeon_sewer','dungeon_mine','dungeon_temple','dungeon_prison',
  'cave_natural','cave_crystal','cave_lava','cave_ice','forest_clearing','forest_dense','swamp',
  'desert','mountain','beach','grassland','snow','tavern_interior','castle_interior','castle_courtyard',
  'street_medieval','street_slum','marketplace','docks','ship_deck','jungle_river','jungle_ruins',
  'jungle_clearing','arcane_library','throne_room','arena','racetrack','graveyard','bridge','custom',
]);
const VALID_MOODS = new Set([
  'bright_day','overcast','golden_hour','twilight','moonlit','torchlit','dark_ambient','magical_glow','ominous','cozy',
]);

async function handleGenerateMap(cmd: GamemasterCommand & { type: 'generate_map' }): Promise<void> {
  const { useMapStore } = await import('../stores/mapStore');

  // Fallback to 'custom'/'magical_glow' if AI sends invalid enum values
  const environment = VALID_ENVIRONMENTS.has(cmd.environment) ? cmd.environment : 'custom';
  const mood = VALID_MOODS.has(cmd.mood) ? cmd.mood : 'magical_glow';

  useMapStore.getState().requestMap({
    source: 'scene-backdrop',
    environment: environment as typeof cmd.environment,
    mood: mood as typeof cmd.mood,
    style: cmd.style,
    prompt: cmd.prompt || `${cmd.environment} ${cmd.mood}`,
    widthFeet: cmd.widthFeet,
    heightFeet: cmd.heightFeet,
  });
}

async function handleAdvanceScene(
  cmd: GamemasterCommand & { type: 'advance_scene' },
  campaignId: string,
): Promise<void> {
  const { useMobStore } = await import('../stores/mobStore');
  const { useCampaignHistoryStore } = await import('../stores/campaignHistoryStore');

  // Archive dead mobs and log kill events
  const { archivedCount, archivedNames } = await useMobStore.getState().archiveDeadMobs();
  for (const name of archivedNames) {
    const data: HistoryEventData = {
      type: 'mob_killed',
      mobId: '',
      mobName: name,
    };
    await useCampaignHistoryStore.getState().addEvent(campaignId, 'mob_killed', data);
  }

  // Remove all remaining mobs (changing locations clears the board)
  await useMobStore.getState().removeAllMobs();

  // Log scene transition
  const transitionData: HistoryEventData = {
    type: 'scene_transition',
    toScene: cmd.name,
    deadMobsArchived: archivedCount,
  };
  await useCampaignHistoryStore.getState().addEvent(campaignId, 'scene_transition', transitionData);

  // Post scene divider
  useChatStore.getState().addSystemMessage(`--- ${cmd.name} — ${cmd.location} ---`);
}

const NPC_TOKEN_COLOR = '#7c6fbd';

async function handleNpcSpeak(cmd: GamemasterCommand & { type: 'npc_speak' }): Promise<void> {
  useChatStore.getState().addNpcMessage({
    npcId: `npc_${cmd.npcName.toLowerCase().replace(/\s+/g, '_')}`,
    npcName: cmd.npcName,
    npcTitle: cmd.npcTitle,
    message: cmd.dialogue,
  });

  if (cmd.createToken) {
    const { useTokenStore } = await import('../stores/tokenStore');
    const { useMapStore } = await import('../stores/mapStore');
    const { generateMissingTokens } = await import('../lib/assetPaths');

    const tokenStore = useTokenStore.getState();
    const existing = Array.from(tokenStore.tokens.values()).find(
      t => t.name.toLowerCase() === cmd.npcName.toLowerCase() && !t.ownerId
    );
    if (existing) return;

    const mapBounds = useMapStore.getState().getMapBoundsInPixels();
    const gridSize = tokenStore.gridSize;
    const mapW = mapBounds?.width ?? 1000;
    const mapH = mapBounds?.height ?? 1000;
    const pos = resolveTokenPosition(cmd.position, 0, 1, 'melee', mapW, mapH, gridSize);

    tokenStore.addToken({
      name: cmd.npcName,
      position: pos,
      visuals: {
        shape: 'circle',
        size: 'medium',
        color: NPC_TOKEN_COLOR,
        showHpBar: false,
        showConditionIcons: false,
        label: cmd.npcName.slice(0, 2).toUpperCase(),
      },
    });

    generateMissingTokens([cmd.npcName], (name, imageUrl) => {
      const tokens = useTokenStore.getState().tokens;
      for (const [, token] of tokens) {
        if (token.name.toLowerCase() === name.toLowerCase()) {
          useTokenStore.getState().updateToken({ id: token.id, visuals: { imageUrl } });
          break;
        }
      }
    });
  }
}

async function handleSyncBoard(cmd: SyncBoardCommand): Promise<void> {
  const { useMobStore } = await import('../stores/mobStore');
  const { useCampaignStore } = await import('../stores/campaignStore');
  const { useMapStore } = await import('../stores/mapStore');
  const { useTokenStore } = await import('../stores/tokenStore');
  const { generateMissingTokens } = await import('../lib/assetPaths');

  const campaignId = useCampaignStore.getState().currentCampaignId;
  if (!campaignId) return;

  const tokenStore = useTokenStore.getState();
  const mapBounds = useMapStore.getState().getMapBoundsInPixels();
  const gridSize = tokenStore.gridSize;
  const mapW = mapBounds?.width ?? 1000;
  const mapH = mapBounds?.height ?? 1000;

  if (!cmd.keepExisting) {
    await useMobStore.getState().removeAllMobs();
    // Remove NPC tokens (no ownerId, not linked to remaining mobs)
    const mobIds = new Set(Array.from(useMobStore.getState().mobs.keys()));
    for (const [tokenId, token] of tokenStore.tokens) {
      if (!token.ownerId && (!token.combatantId || !mobIds.has(token.combatantId))) {
        tokenStore.removeToken(tokenId);
      }
    }
  }

  // Spawn mobs
  for (const mob of cmd.mobs) {
    const positioning: 'melee' | 'ranged' = (mob.dex ?? 10) >= (mob.str ?? 10) + 4 ? 'ranged' : 'melee';
    for (let i = 0; i < mob.count; i++) {
      const pos = resolveTokenPosition(mob.position, i, mob.count, positioning, mapW, mapH, gridSize);
      await useMobStore.getState().spawnMob({
        campaignId,
        name: mob.name,
        ac: mob.ac,
        maxHp: mob.maxHp,
        abilities: {
          str: mob.str ?? 10,
          dex: mob.dex ?? 10,
          con: mob.con ?? 10,
          int: mob.int ?? 10,
          wis: mob.wis ?? 10,
          cha: mob.cha ?? 10,
        },
      }, {
        position: pos,
        rollInitiative: true,
      });
    }
  }

  // Create NPC tokens
  for (let i = 0; i < cmd.npcs.length; i++) {
    const npc = cmd.npcs[i];
    const pos = resolveTokenPosition(npc.position, i, cmd.npcs.length, 'melee', mapW, mapH, gridSize);
    tokenStore.addToken({
      name: npc.name,
      position: pos,
      visuals: {
        shape: 'circle',
        size: 'medium',
        color: NPC_TOKEN_COLOR,
        showHpBar: false,
        showConditionIcons: false,
        label: npc.name.slice(0, 2).toUpperCase(),
      },
    });
    generateMissingTokens([npc.name], (name, imageUrl) => {
      const tokens = useTokenStore.getState().tokens;
      for (const [, token] of tokens) {
        if (token.name.toLowerCase() === name.toLowerCase()) {
          useTokenStore.getState().updateToken({ id: token.id, visuals: { imageUrl } });
          break;
        }
      }
    });
  }

  if (cmd.description) {
    useChatStore.getState().addSystemMessage(cmd.description);
  }
}

async function handleStartCombat(campaignId: string): Promise<void> {
  const { useEncounterStore } = await import('../stores/encounterStore');
  const { useMobStore } = await import('../stores/mobStore');
  const { useCharacterStore } = await import('../stores/characterStore');

  const encounter = useEncounterStore.getState();

  // Create encounter if none active
  if (!encounter.activeEncounter) {
    encounter.startEncounter('solo-combat', campaignId);
  }

  // Add PC participant (roll initiative for them)
  const chars = useCharacterStore.getState().characters;
  for (const [id, char] of chars) {
    if (char.isNpc) continue;
    const dexMod = Math.floor(((char.abilityScores?.dexterity ?? 10) - 10) / 2);
    const initRoll = Math.floor(Math.random() * 20) + 1 + dexMod;
    useEncounterStore.getState().addParticipant(id, 'character', char.name, initRoll, dexMod);
  }

  // Add mob participants (use their already-rolled initiative or roll now)
  const mobs = useMobStore.getState().mobs;
  for (const [, mob] of mobs) {
    if (mob.dead) continue;
    const dexMod = Math.floor(((mob.dexterity ?? 10) - 10) / 2);
    const initRoll = Math.floor(Math.random() * 20) + 1 + dexMod;
    useEncounterStore.getState().addParticipant(mob.id, 'mob', mob.name, initRoll, dexMod);
  }

  // Start combat
  useEncounterStore.getState().beginCombat();

  useChatStore.getState().addSystemMessage('Roll for initiative!');
}

async function handleEndCombat(cmd: GamemasterCommand & { type: 'end_combat' }): Promise<void> {
  const { useEncounterStore } = await import('../stores/encounterStore');
  const { useMobStore } = await import('../stores/mobStore');
  const { useInitiativeStore } = await import('../stores/initiativeStore');

  // End the encounter in DB + clear initiative + remove dead mobs
  useEncounterStore.getState().endEncounter();
  useInitiativeStore.getState().clearAllEntries();
  await useMobStore.getState().removeAllMobs();

  if (cmd.summary) {
    useChatStore.getState().addSystemMessage(`Combat ended. ${cmd.summary}`);
  } else {
    useChatStore.getState().addSystemMessage('Combat has ended.');
  }
}

async function handleResetBoard(_reason?: string): Promise<void> {
  const { useEncounterStore } = await import('../stores/encounterStore');
  const { useMobStore } = await import('../stores/mobStore');
  const { useInitiativeStore } = await import('../stores/initiativeStore');

  // Full wipe: encounter → initiative → mobs (removeAllMobs also removes mob tokens)
  useEncounterStore.getState().endEncounter();
  useInitiativeStore.getState().clearAllEntries();
  await useMobStore.getState().removeAllMobs();

  useChatStore.getState().addSystemMessage(_reason || 'The board has been cleared.');
}

async function handleAwardXp(cmd: GamemasterCommand & { type: 'award_xp' }): Promise<void> {
  const { useCharacterStore } = await import('../stores/characterStore');
  const charStore = useCharacterStore.getState();

  // Award XP to all active party members (non-NPC)
  const party = charStore.getActivePartyMembers().filter((c) => !c.isNpc);
  const levelUps: string[] = [];

  for (const char of party) {
    const { leveledUp } = charStore.awardXp(char.id, cmd.amount);
    if (leveledUp) levelUps.push(char.name);
  }

  useChatStore.getState().addSystemMessage(`Gained ${cmd.amount} XP — ${cmd.reason}`);

  for (const name of levelUps) {
    useChatStore.getState().addSystemMessage(`${name} has enough XP to level up!`);
  }
}

function handleDiscoverClue(cmd: GamemasterCommand & { type: 'discover_clue' }): void {
  useChatStore.getState().addSystemMessage(`Clue discovered: ${cmd.front}`);
}

function handleUpdateQuest(cmd: GamemasterCommand & { type: 'update_quest' }): void {
  const statusEmoji: Record<string, string> = {
    new: 'New quest',
    active: 'Quest updated',
    completed: 'Quest completed',
    failed: 'Quest failed',
  };
  useChatStore.getState().addSystemMessage(
    `${statusEmoji[cmd.status] || 'Quest'}: ${cmd.name}${cmd.description ? ` — ${cmd.description}` : ''}`
  );
}

function handlePlaySound(cmd: GamemasterCommand & { type: 'play_sound' }): void {
  // Sound system integration — lazy import to avoid loading audio on init
  console.log('[CommandBridge] play_sound:', cmd.sound, cmd.category);
}

function handlePresentChoices(cmd: GamemasterCommand & { type: 'present_choices' }): void {
  useChatStore.getState().addMessage({
    sender: 'Dungeon Master',
    message: cmd.prompt || '',
    type: 'ai_dm_choices',
    choiceButtons: cmd.choices,
  });
}

// =============================================================================
// CAMPAIGN MAP HANDLERS
// =============================================================================

async function handleSetupCampaignMap(
  cmd: GamemasterCommand & { type: 'setup_campaign_map' },
  campaignId: string,
): Promise<void> {
  const { useCampaignMapStore } = await import('../stores/campaignMapStore');

  // Generate a parchment-style campaign map image
  const mapPrompt = `Fantasy parchment campaign map, ${cmd.theme}, top-down cartography style, aged paper texture, hand-drawn mountains rivers and forests, no text or labels, no grid`;
  let imageUrl: string;
  let width = 1024;
  let height = 1024;

  try {
    const result = await trpc.maps.generate.mutate({
      prompt: {
        environment: 'grassland' as const,
        mood: 'golden_hour' as const,
        style: 'top_down_painted' as const,
        userPrompt: mapPrompt,
        widthFeet: 100,
        heightFeet: 100,
      },
      provider: 'auto',
    });
    if (result.success && result.imageUrl) {
      imageUrl = result.imageUrl;
      width = result.width || 1024;
      height = result.height || 1024;
    } else {
      console.warn('[CommandBridge] Campaign map generation failed, skipping');
      return;
    }
  } catch (err) {
    console.error('[CommandBridge] Campaign map generation error:', err);
    return;
  }

  // Create the campaign map record
  const campaignMap = await useCampaignMapStore.getState().uploadCampaignMapImage(
    campaignId,
    imageUrl,
    cmd.name,
    width,
    height,
  );
  if (!campaignMap) {
    console.warn('[CommandBridge] Failed to create campaign map record');
    return;
  }

  // Add pins
  for (const pin of cmd.pins) {
    await useCampaignMapStore.getState().addPin({
      campaignMapId: campaignMap.id,
      x: pin.x,
      y: pin.y,
      name: pin.name,
      description: pin.description,
      iconType: (pin.type as 'town' | 'city' | 'village' | 'dungeon' | 'cave' | 'quest' | 'encounter' | 'landmark' | 'shop' | 'tavern' | 'temple' | 'camp' | 'ruins' | 'portal') || 'landmark',
      state: pin.state || 'hidden',
      tags: [],
    });
  }

  // Generate fog regions (4x4 grid)
  await useCampaignMapStore.getState().generateRegions();

  // Switch to campaign map view
  useCampaignMapStore.getState().setMapMode('campaign');

  console.log('[CommandBridge] Campaign map created:', cmd.name, 'with', cmd.pins.length, 'pins');
}

async function handleRevealLocation(
  cmd: GamemasterCommand & { type: 'reveal_location' },
): Promise<void> {
  const { useCampaignMapStore } = await import('../stores/campaignMapStore');
  const state = useCampaignMapStore.getState();

  // Find pin by name (case-insensitive)
  const searchName = cmd.pinName.toLowerCase();
  let foundPin: { id: string; regionId?: string } | null = null;

  for (const [, pin] of state.pins) {
    if (pin.name.toLowerCase() === searchName) {
      foundPin = { id: pin.id, regionId: pin.regionId };
      break;
    }
  }

  if (!foundPin) {
    console.warn('[CommandBridge] reveal_location: pin not found:', cmd.pinName);
    return;
  }

  // Reveal the pin
  await state.setPinState(foundPin.id, 'discovered');

  // Reveal fog region if requested (default true)
  if (cmd.revealFog !== false && foundPin.regionId) {
    await state.revealRegion(foundPin.regionId);
  }

  console.log('[CommandBridge] Revealed location:', cmd.pinName);
}

async function handleMoveParty(
  cmd: GamemasterCommand & { type: 'move_party' },
): Promise<void> {
  const { useCampaignMapStore } = await import('../stores/campaignMapStore');
  const state = useCampaignMapStore.getState();

  // Find pin by name (case-insensitive)
  const searchName = cmd.destination.toLowerCase();
  let foundPin: { id: string; state: string; regionId?: string } | null = null;

  for (const [, pin] of state.pins) {
    if (pin.name.toLowerCase() === searchName) {
      foundPin = { id: pin.id, state: pin.state, regionId: pin.regionId };
      break;
    }
  }

  if (!foundPin) {
    console.warn('[CommandBridge] move_party: destination not found:', cmd.destination);
    return;
  }

  // Auto-reveal if hidden
  if (foundPin.state === 'hidden') {
    await state.setPinState(foundPin.id, 'discovered');
    if (foundPin.regionId) {
      await state.revealRegion(foundPin.regionId);
    }
  }

  // Mark as active and move party
  await state.setPinState(foundPin.id, 'active');
  await state.setPartyLocation(foundPin.id);

  console.log('[CommandBridge] Party moved to:', cmd.destination);
}

async function handlePlaceParty(
  cmd: GamemasterCommand & { type: 'place_party' },
): Promise<void> {
  const { useTokenStore } = await import('../stores/tokenStore');
  const { useMapStore } = await import('../stores/mapStore');
  const { useCharacterStore } = await import('../stores/characterStore');

  const tokenStore = useTokenStore.getState();
  const mapBounds = useMapStore.getState().getMapBoundsInPixels();
  const gridSize = tokenStore.gridSize;
  const mapW = mapBounds?.width ?? 1000;
  const mapH = mapBounds?.height ?? 1000;

  // Find all PC tokens (tokens with an ownerId that matches a non-NPC character)
  const chars = useCharacterStore.getState().characters;
  const pcIds = new Set(Array.from(chars.values()).filter((c) => !c.isNpc).map((c) => c.id));
  const pcTokens = Array.from(tokenStore.tokens.values()).filter(
    (t) => t.ownerId && pcIds.has(t.ownerId),
  );

  if (pcTokens.length === 0) {
    console.warn('[CommandBridge] place_party: no PC tokens found');
    return;
  }

  // Resolve positions based on command
  const pos = cmd.position || 'south';
  let positions: Array<{ x: number; y: number }>;

  if (pos === 'south') {
    positions = getPartyPositions(pcTokens.length, mapW, mapH, gridSize);
  } else {
    const yFrac = pos === 'north' ? 0.15 : pos === 'center' ? 0.5 : 0.75;
    const totalWidth = pcTokens.length * gridSize;
    const startX = (mapW - totalWidth) / 2;
    positions = pcTokens.map((_, i) => ({
      x: startX + i * gridSize,
      y: mapH * yFrac,
    }));
  }

  // Move each PC token
  pcTokens.forEach((token, i) => {
    if (positions[i]) {
      tokenStore.moveToken(token.id, positions[i]);
    }
  });

  console.log('[CommandBridge] Party placed at:', pos, pcTokens.length, 'tokens');
}
